// Tier 2 practiceSchedule migration, Phase B: the real intended replacement for the cancellations
// portion of App.jsx's watchDoc mechanism — currently:
//   watchDoc("practiceSchedule", setPracticeSchedule, d=>({..., cancellations:d.cancellations||[]}))
// which subscribes to the single warriors/practiceSchedule document and reads its `cancellations`
// array field. This module instead subscribes to the new top-level `practiceCancellations`
// collection (one document per cancellation, doc ID = the cancellation's own pc_* id — see
// scripts/migrate-practiceschedule.cjs) and reconstructs an equivalent in-memory array. Sibling to
// practiceGroupsCollection.js — two separate collections, two separate files, matching the
// established one-file-per-collection convention.
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Converts one practiceCancellations/{id} document into the shape the app's in-memory
// `cancellations` array has always used. `nodeId` USED to be dropped here too — fixed proactively
// alongside players/teams/coaches/practiceGroups, same reasoning (see practiceGroupsCollection.js).
// The one write site that creates a cancellation (src/App.jsx) builds its payload from an
// explicitly-constructed object, never spread from an existing cancellation, so carrying nodeId
// through here doesn't leak it into that write. `id` is already the doc ID (pc_* format), no
// conversion needed.
export function practiceCancellationsDocToRecord(docId, data) {
  return { ...data, id: docId };
}

// Live-subscription replacement for the cancellations portion of watchDoc("practiceSchedule", ...).
// Kept as the unscoped variant for coach/parent (neither has a node concept), matching
// practiceCancellations' read rule staying isAllowed()||isParent() — admin now uses
// watchPracticeCancellationsCollectionForNodes below.
export function watchPracticeCancellationsCollection(setter) {
  return onSnapshot(collection(db, 'practiceCancellations'), (snap) => {
    setter(snap.docs.map((d) => practiceCancellationsDocToRecord(d.id, d.data())));
  });
}

// Node-scoped variant (mirroring watchTeamsCollectionForNodes/watchCoachesCollectionForNodes
// exactly) — constrained via where('nodeId','in',nodeIds) to a caller-supplied permitted-node list.
// Used for the admin role only; practiceCancellations' READ rule itself stays unscoped for everyone
// this pass (see firestore.rules) — this is the admin management view narrowing itself for UX
// consistency, not a security boundary the rule enforces. Same 30-value 'in' ceiling as the others.
export function watchPracticeCancellationsCollectionForNodes(nodeIds, setter) {
  return onSnapshot(query(collection(db, 'practiceCancellations'), where('nodeId', 'in', nodeIds)), (snap) => {
    setter(snap.docs.map((d) => practiceCancellationsDocToRecord(d.id, d.data())));
  });
}
